if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => this.bindThumbnail(mediaToSwitch));
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.initGalleryFlickity();
      }

      connectedCallback() {
        this.initShadeFilter();
      }

      disconnectedCallback() {
        this.variantPicker?.removeEventListener('change', this.onVariantPickerChange);
        this.variantPicker = null;
      }

      bindThumbnail(mediaToSwitch) {
        mediaToSwitch
          .querySelector('button')
          .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      }

      // Opt-in via data-shade-filter (see 'media-shade-map'): only the selected shade's media is rendered,
      // the rest waits in <template data-shade-pool> and is swapped in here when the variant picker changes.
      initShadeFilter() {
        if (!this.hasAttribute('data-shade-filter') || this.variantPicker) return;

        this.variantPicker = document.querySelector(
          `variant-radios[data-section="${this.dataset.sectionId}"], variant-selects[data-section="${this.dataset.sectionId}"]`
        );
        if (!this.variantPicker) return;

        try {
          this.shadeHandles = JSON.parse(this.dataset.shadeHandles);
          this.thumbnailLabels = JSON.parse(this.dataset.thumbnailLabels);
        } catch (error) {
          console.error('media-gallery: invalid shade filter data', error);
          return;
        }

        this.currentShade = this.dataset.selectedShade;
        this.shadePools = {
          main: this.querySelector('template[data-shade-pool="main"]'),
          thumbnails: this.querySelector('template[data-shade-pool="thumbnails"]'),
        };
        this.onVariantPickerChange = this.onVariantPickerChange.bind(this);
        this.variantPicker.addEventListener('change', this.onVariantPickerChange);
      }

      onVariantPickerChange() {
        const shadeInput =
          this.variantPicker.querySelector('fieldset input[type="radio"]:checked') ||
          this.variantPicker.querySelector('select');
        const shade = shadeInput && this.shadeHandles[shadeInput.value];
        if (shade) this.showShade(shade);
      }

      showShade(shade) {
        if (shade === this.currentShade) return;
        this.currentShade = shade;

        const incomingSlides = this.swapShadeCells(this.elements.viewer.slider, this.shadePools.main, shade);
        if (this.elements.thumbnails) {
          this.swapShadeCells(this.elements.thumbnails.slider, this.shadePools.thumbnails, shade).forEach(
            (thumbnail) => {
              if (thumbnail.dataset.shadeBound) return;
              thumbnail.dataset.shadeBound = 'true';
              this.bindThumbnail(thumbnail);
            }
          );
          this.renumberThumbnails();
        }

        this.updateSlideCounter();
        this.toggleModalShade(shade);
        if (incomingSlides.length) this.setActiveMedia(incomingSlides[0].dataset.mediaId, false);
      }

      // Moves the current shade's cells back into the pool and inserts the new shade's cells at their
      // original position (data-media-order), through Flickity when it governs the list.
      swapShadeCells(list, pool, shade) {
        if (!list) return [];
        const flkty = typeof Flickity !== 'undefined' && Flickity.data(list);
        const cells = () => (flkty ? flkty.getCellElements() : Array.from(list.children));

        const outgoing = cells().filter((cell) => cell.dataset.shade && cell.dataset.shade !== shade);
        if (flkty) {
          flkty.remove(outgoing);
        } else {
          outgoing.forEach((cell) => cell.remove());
        }
        outgoing.forEach((cell) => {
          cell.classList.remove('is-active');
          cell.querySelector('button[aria-current]')?.removeAttribute('aria-current');
        });

        const incoming = pool ? Array.from(pool.content.querySelectorAll(`[data-shade="${CSS.escape(shade)}"]`)) : [];
        if (pool) pool.content.append(...outgoing);

        incoming.forEach((cell) => {
          const order = Number(cell.dataset.mediaOrder);
          const currentCells = cells();
          const index = currentCells.findIndex((currentCell) => Number(currentCell.dataset.mediaOrder) > order);
          if (flkty) {
            flkty.insert(cell, index === -1 ? currentCells.length : index);
          } else {
            list.insertBefore(cell, currentCells[index] ?? null);
          }
        });

        return incoming;
      }

      renumberThumbnails() {
        const counters = { image: 0, video: 0, model: 0 };
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const type = thumbnail.dataset.mediaType === 'external_video' ? 'video' : thumbnail.dataset.mediaType;
          if (!(type in counters)) return;
          counters[type] += 1;
          thumbnail.dataset.mediaPosition = counters[type];
          thumbnail.querySelector('button')?.setAttribute('aria-label', this.thumbnailLabels[type].replace('[index]', counters[type]));
        });
      }

      updateSlideCounter() {
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (total) total.textContent = this.elements.viewer.slider.querySelectorAll('.product__media-item').length;
        if (typeof Flickity !== 'undefined' && Flickity.data(this.elements.viewer.slider)) return;
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      toggleModalShade(shade) {
        document.querySelectorAll(`#ProductModal-${this.dataset.sectionId} [data-shade]`).forEach((media) => {
          media.hidden = media.dataset.shade !== shade;
        });
      }

      // Replaces the native scroll-snap main viewer + thumbnail slider with two
      // linked Flickity instances (already loaded theme-wide, not a new dependency):
      // the thumbnail track is Flickity's documented "nav" carousel for the main
      // "content" carousel (the `asNavFor` option) — clicking/dragging either one
      // smoothly slides the other into sync, matching the previous hardcoded
      // gallery's feel (see the now-unused assets/color-box-arrows.js/.css, which
      // this ports and wires up to the native media-gallery data instead) without
      // Dawn's abrupt display:none slide toggle or its scroll-reveal fade-in.
      initGalleryFlickity() {
        if (typeof Flickity === 'undefined') return;
        const mainContainer = this.elements.viewer.slider;
        const thumbContainer = this.elements.thumbnails.slider;
        if (!mainContainer || !thumbContainer || Flickity.data(mainContainer)) return;

        const mainFlkty = new Flickity(mainContainer, {
          cellAlign: 'left',
          contain: true,
          draggable: true,
          pageDots: false,
          prevNextButtons: false,
          imagesLoaded: true,
        });

        mainFlkty.on('select', () => {
          const cell = mainFlkty.selectedElement;
          if (!cell) return;
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
          cell.classList.add('is-active');
          this.playActiveMedia(cell);
          this.preventStickyHeader();

          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${cell.dataset.mediaId}"]`);
          if (!activeThumbnail) return;
          this.elements.thumbnails.querySelectorAll('button').forEach((btn) => btn.removeAttribute('aria-current'));
          activeThumbnail.querySelector('button').setAttribute('aria-current', true);
          this.announceLiveRegion(cell, activeThumbnail.dataset.mediaPosition);
        });

        new Flickity(thumbContainer, {
          cellAlign: 'left',
          contain: true,
          draggable: true,
          pageDots: false,
          prevNextButtons: false,
          imagesLoaded: true,
          asNavFor: `#${CSS.escape(mainContainer.id)}`,
        });

        this.bindMainImageArrows(mainFlkty);
      }

      // Custom prev/next arrows overlaid on the main image, driven by the thumbnail
      // Flickity instance — mirrors the old gallery's .main-slider-arrow buttons.
      bindMainImageArrows(flkty) {
        if (this.elements.viewer.querySelector('.main-slider-arrow')) return;

        const prevBtn = document.createElement('button');
        const nextBtn = document.createElement('button');
        prevBtn.type = 'button';
        nextBtn.type = 'button';
        prevBtn.className = 'main-slider-arrow main-slider-arrow--prev flickity-button flickity-prev-next-button';
        nextBtn.className = 'main-slider-arrow main-slider-arrow--next flickity-button flickity-prev-next-button';
        prevBtn.setAttribute('aria-label', window.accessibilityStrings?.previousSlide || 'Previous slide');
        nextBtn.setAttribute('aria-label', window.accessibilityStrings?.nextSlide || 'Next slide');
        prevBtn.innerHTML =
          '<svg class="flickity-button-icon" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow"></path></svg>';
        nextBtn.innerHTML =
          '<svg class="flickity-button-icon" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180)"></path></svg>';

        prevBtn.addEventListener('click', () => flkty.previous());
        nextBtn.addEventListener('click', () => flkty.next());

        this.elements.viewer.append(prevBtn, nextBtn);
      }

      onSlideChanged(event) {
        // Dead in practice once initGalleryFlickity() takes over (its own 'select'
        // handler replaces this) — kept only as a fallback for the rare case where
        // Flickity failed to initialize. SliderComponent's own ResizeObserver can
        // still fire this event with no resolvable current slide once Flickity's
        // absolute-positioned cells are in play, so guard against that.
        if (!event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        // When Flickity governs the main viewer (see initGalleryFlickity), let it
        // own the transition — its own 'select' handler performs the is-active
        // swap/play/announce side effects. This also covers the variant-change
        // path (VariantSelects.updateMedia in global.js calls this with
        // prepend:true), which no longer needs the DOM-reordering "prepend" hack:
        // Flickity can smoothly select any cell directly, regardless of position.
        const mainFlkty = typeof Flickity !== 'undefined' && Flickity.data(this.elements.viewer.slider);
        if (mainFlkty) {
          const index = mainFlkty.cells.findIndex((cell) => cell.element === activeMedia);
          if (index > -1) mainFlkty.select(index);
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // When Flickity governs the thumbnail track, it owns cell positioning —
        // the native scrollTo/offsetLeft math below no longer reflects real layout.
        const flkty = typeof Flickity !== 'undefined' && Flickity.data(this.elements.thumbnails.slider);
        if (flkty) {
          const index = flkty.cells.findIndex((cell) => cell.element === thumbnail);
          if (index > -1 && index !== flkty.selectedIndex) flkty.select(index, false, true);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
